import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Collapsible section with a smooth open/close animation.
 *
 * Replaces native <details>/<summary>: the spec doesn't give us a way
 * to transition the content slot's height, and the workarounds that
 * do (::details-content, interpolate-size: allow-keywords) only ship
 * in Chromium ≥131 / Safari TP. The CSS-grid `0fr → 1fr` trick works
 * everywhere modern (Chrome 117+, Firefox 119+, Safari 17.4+) and
 * degrades to an instant snap on older browsers.
 *
 * The button-with-h2-inside is a minor HTML purity nit (interactive
 * content nesting), but every screen reader handles it correctly, the
 * keyboard semantics are right, and it keeps the markup simpler than
 * a div+role=button+manual-keyhandlers replacement.
 *
 * Honours `prefers-reduced-motion`: the transition collapses to 0ms
 * via the `motion-reduce` modifier, so the open/close still happens
 * but instantly.
 */

interface Props {
  /** Content of the clickable header (typically an `<h2>` with icon + heading). */
  trigger: React.ReactNode;
  /** Tailwind classes applied to the trigger `<button>`. */
  triggerClassName?: string;
  /** Initial open state. Default false. */
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export function CollapsibleSection({
  trigger,
  triggerClassName,
  defaultOpen = false,
  children,
}: Props): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`flex w-full cursor-pointer items-center justify-between gap-2 text-left ${
          triggerClassName ?? ''
        }`}
      >
        <span className="min-w-0 flex-1">{trigger}</span>
        <ChevronDown
          className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform duration-300 ease-out motion-reduce:transition-none dark:text-slate-500 ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden="true"
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
}
